import { useEffect, useState } from "react";
import axios from "axios";
import { Trash2, RefreshCw, CheckCircle2, PlayCircle, XCircle, Mail, ChevronDown, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { useAuth, formatApiError } from "@/lib/auth";
import { API } from "@/lib/site";

function fmtDate(s) { if (!s) return "—"; try { return new Date(s).toLocaleString(); } catch { return s; } }

const STATUS_STYLE = {
  pending:     { bg: "bg-amber-50",    text: "text-amber-800",   border: "border-amber-200",   dot: "bg-amber-500" },
  in_progress: { bg: "bg-sky-50",      text: "text-sky-700",     border: "border-sky-200",     dot: "bg-sky-500" },
  completed:   { bg: "bg-emerald-50",  text: "text-emerald-700", border: "border-emerald-200", dot: "bg-emerald-500" },
  cancelled:   { bg: "bg-slate-100",   text: "text-slate-500",   border: "border-slate-200",   dot: "bg-slate-400" },
};

function StatusPill({ status }) {
  const s = STATUS_STYLE[status] || STATUS_STYLE.pending;
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${s.bg} ${s.text} border ${s.border}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${s.dot}`} />
      {status.replace("_", " ").toUpperCase()}
    </span>
  );
}

function CountPill({ label, count, status }) {
  const s = STATUS_STYLE[status] || STATUS_STYLE.pending;
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[12px] font-medium ${s.bg} ${s.text} border ${s.border}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${s.dot}`} />
      {label}: <strong className="ml-0.5">{count}</strong>
    </span>
  );
}

export default function DeletionRequestsPanel() {
  const { headers } = useAuth();
  const [data, setData] = useState({ items: [], counts: {}, total: 0 });
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [acting, setActing] = useState({});

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const qs = statusFilter ? `?status_filter=${statusFilter}` : "";
        const { data: res } = await axios.get(`${API}/admin/deletion-requests${qs}`, { headers: headers() });
        if (alive) setData(res);
      } catch (err) {
        if (alive) toast.error(formatApiError(err));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const refresh = async () => {
    setLoading(true);
    try {
      const qs = statusFilter ? `?status_filter=${statusFilter}` : "";
      const { data: res } = await axios.get(`${API}/admin/deletion-requests${qs}`, { headers: headers() });
      setData(res);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (req, newStatus, notes) => {
    setActing((a) => ({ ...a, [req.request_id]: newStatus }));
    try {
      const { data: updated } = await axios.patch(
        `${API}/admin/deletion-requests/${req.request_id}`,
        { status: newStatus, notes },
        { headers: headers() },
      );
      setData((d) => ({
        ...d,
        items: d.items.map((r) => (r.request_id === req.request_id ? updated : r)),
      }));
      toast.success(`Marked ${updated.email} as ${newStatus.replace("_", " ")}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setActing((a) => { const c = { ...a }; delete c[req.request_id]; return c; });
    }
  };

  const removeRow = async (req) => {
    if (!window.confirm(`Permanently delete this audit-log entry for ${req.email}?\nThis cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/deletion-requests/${req.request_id}`, { headers: headers() });
      setData((d) => ({ ...d, items: d.items.filter((r) => r.request_id !== req.request_id) }));
      toast.success("Audit entry deleted");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <section className="mt-10" data-testid="admin-deletion-requests-panel">
      <div className="flex items-baseline justify-between mb-4 flex-wrap gap-2">
        <div>
          <h2 className="font-display text-[22px] text-[#0A1128]">GDPR deletion requests</h2>
          <p className="text-[12px] text-slate-500 mt-1">
            Submissions from the public <code className="font-mono">/delete-account</code> page. Complete within 30 days per GDPR.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <CountPill label="Pending" count={data.counts.pending || 0} status="pending" />
          <CountPill label="In progress" count={data.counts.in_progress || 0} status="in_progress" />
          <CountPill label="Completed" count={data.counts.completed || 0} status="completed" />
          <button
            onClick={refresh}
            data-testid="del-refresh"
            className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white text-[#0A1128] text-[13px] font-medium px-4 py-2 hover:border-slate-400"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>
      </div>

      {/* Filter chips */}
      <div className="mb-4 flex items-center gap-2 flex-wrap text-[12px]">
        <span className="text-slate-500">Filter:</span>
        {[
          { v: "", l: "All" },
          { v: "pending", l: "Pending" },
          { v: "in_progress", l: "In progress" },
          { v: "completed", l: "Completed" },
          { v: "cancelled", l: "Cancelled" },
        ].map((opt) => (
          <button
            key={opt.v}
            onClick={() => setStatusFilter(opt.v)}
            data-testid={`del-filter-${opt.v || "all"}`}
            className={`rounded-full px-3 py-1 font-medium border transition-colors ${
              statusFilter === opt.v
                ? "bg-[#0A1128] text-white border-[#0A1128]"
                : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
            }`}
          >
            {opt.l}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="rounded-2xl border border-slate-200 bg-white af-shadow-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-[13.5px]" data-testid="del-table">
            <thead className="bg-slate-50 text-slate-500 text-left">
              <tr>
                <th className="font-medium px-4 py-3 w-8"></th>
                <th className="font-medium px-4 py-3">Email / Name</th>
                <th className="font-medium px-4 py-3">Reference</th>
                <th className="font-medium px-4 py-3">Submitted</th>
                <th className="font-medium px-4 py-3">Status</th>
                <th className="font-medium px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.items.map((req) => {
                const isOpen = expanded === req.request_id;
                return (
                  <>
                    <tr key={req.request_id} data-testid={`del-row-${req.request_id}`} className="hover:bg-slate-50">
                      <td className="px-2 py-3">
                        <button
                          onClick={() => setExpanded(isOpen ? null : req.request_id)}
                          className="text-slate-500 hover:text-[#0A1128]"
                        >
                          {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <p className="font-medium text-[#0A1128]">{req.email}</p>
                        {req.name && <p className="text-[12px] text-slate-500">{req.name}</p>}
                      </td>
                      <td className="px-4 py-3 font-mono text-[11.5px] text-slate-600">{req.request_id}</td>
                      <td className="px-4 py-3 text-[12px] text-slate-500">
                        {fmtDate(req.created_at)}
                        {req.submission_count > 1 && (
                          <span className="ml-1 text-amber-700">· resubmitted {req.submission_count}×</span>
                        )}
                      </td>
                      <td className="px-4 py-3"><StatusPill status={req.status} /></td>
                      <td className="px-4 py-3 text-right">
                        <div className="inline-flex items-center gap-1 flex-wrap justify-end">
                          {req.status === "pending" && (
                            <button
                              onClick={() => updateStatus(req, "in_progress")}
                              disabled={acting[req.request_id]}
                              data-testid={`del-mark-progress-${req.request_id}`}
                              className="inline-flex items-center gap-1 rounded-md border border-sky-300 bg-white text-sky-700 text-[11px] font-medium px-2 py-1 hover:bg-sky-50"
                            >
                              <PlayCircle className="h-3 w-3" /> Start
                            </button>
                          )}
                          {req.status !== "completed" && req.status !== "cancelled" && (
                            <button
                              onClick={() => updateStatus(req, "completed")}
                              disabled={acting[req.request_id]}
                              data-testid={`del-mark-done-${req.request_id}`}
                              className="inline-flex items-center gap-1 rounded-md border border-emerald-300 bg-white text-emerald-700 text-[11px] font-medium px-2 py-1 hover:bg-emerald-50"
                            >
                              <CheckCircle2 className="h-3 w-3" /> Done
                            </button>
                          )}
                          {req.status === "pending" && (
                            <button
                              onClick={() => updateStatus(req, "cancelled")}
                              disabled={acting[req.request_id]}
                              data-testid={`del-mark-cancel-${req.request_id}`}
                              className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white text-slate-600 text-[11px] font-medium px-2 py-1 hover:bg-slate-50"
                            >
                              <XCircle className="h-3 w-3" /> Cancel
                            </button>
                          )}
                          <a
                            href={`mailto:${req.email}?subject=Re: AtlasForge deletion request ${req.request_id}`}
                            data-testid={`del-email-${req.request_id}`}
                            className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white text-slate-700 text-[11px] font-medium px-2 py-1 hover:border-slate-400"
                          >
                            <Mail className="h-3 w-3" /> Email
                          </a>
                          <button
                            onClick={() => removeRow(req)}
                            data-testid={`del-remove-${req.request_id}`}
                            className="inline-flex items-center gap-1 rounded-md border border-rose-300 bg-white text-rose-700 text-[11px] font-medium px-2 py-1 hover:bg-rose-50"
                          >
                            <Trash2 className="h-3 w-3" />
                          </button>
                        </div>
                      </td>
                    </tr>
                    {isOpen && (
                      <tr key={`${req.request_id}-detail`} className="bg-slate-50/60">
                        <td colSpan={6} className="px-4 py-4">
                          <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-[12.5px]">
                            <div>
                              <span className="text-slate-500">Reason:</span>{" "}
                              <span className="text-[#0A1128]">{req.reason || "(none given)"}</span>
                            </div>
                            <div>
                              <span className="text-slate-500">Last 4 phone:</span>{" "}
                              <span className="font-mono text-[#0A1128]">{req.last_4_phone || "—"}</span>
                            </div>
                            <div>
                              <span className="text-slate-500">Submitted IP:</span>{" "}
                              <span className="font-mono text-[#0A1128]">{req.submitted_ip || "—"}</span>
                            </div>
                            <div>
                              <span className="text-slate-500">Last updated:</span>{" "}
                              <span className="text-[#0A1128]">{fmtDate(req.updated_at)}</span>
                            </div>
                            {req.completed_at && (
                              <>
                                <div>
                                  <span className="text-slate-500">Completed at:</span>{" "}
                                  <span className="text-[#0A1128]">{fmtDate(req.completed_at)}</span>
                                </div>
                                <div>
                                  <span className="text-slate-500">Completed by:</span>{" "}
                                  <span className="text-[#0A1128]">{req.completed_by || "—"}</span>
                                </div>
                              </>
                            )}
                            {req.notes && (
                              <div className="sm:col-span-2">
                                <span className="text-slate-500">Internal notes:</span>{" "}
                                <span className="text-[#0A1128]">{req.notes}</span>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                );
              })}
              {!loading && data.items.length === 0 && (
                <tr>
                  <td colSpan={6} className="p-6 text-center text-slate-500 text-[14px]">
                    No deletion requests {statusFilter ? `with status "${statusFilter}"` : "yet"}.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}
